import { splitKeyAlias } from "./tags";
import type {
  AvailableUsageDimensions,
  KeyOrigin,
  TagUsageRow,
  TeamWindowUsage,
  UsageDay,
  UsageDimensionRow,
  UsageMetrics,
} from "./types";

/**
 * Parse the proxy's daily-activity payloads.
 *
 * ## The one rule that makes these numbers correct
 *
 * `/tag/daily/activity` reports each day's roll-up in `results[].metrics`, and an overall
 * roll-up in `metadata.total_*`. **Both are wrong when more than one tag is queried.** A
 * request tagged `[org:X, user:Y]` writes two `LiteLLM_DailyTagSpend` rows, so it is counted
 * twice in any roll-up spanning both. LiteLLM knows this — it passes
 * `metadata_metrics_func=None` on this endpoint for exactly this reason.
 *
 * So the only trustworthy source is `breakdown.entities[<tag>]`, which is per-tag and
 * therefore counts each request once. These parsers read that and nothing else.
 *
 * The one carve-out: `metadata.total_pages` is pagination, not a metric, and *is*
 * trustworthy. Stated explicitly because the rule above otherwise reads as "never touch
 * metadata", and the pagination loop would be the first casualty.
 */

const EMPTY_METRICS: UsageMetrics = {
  spend: 0,
  promptTokens: 0,
  completionTokens: 0,
  cacheReadTokens: 0,
  cacheCreationTokens: 0,
  requests: 0,
  successfulRequests: 0,
  failedRequests: 0,
};

export function emptyMetrics(): UsageMetrics {
  return { ...EMPTY_METRICS };
}

/** Coerce leniently: an upstream rename should degrade a field to 0, never throw. */
function num(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function record(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : {};
}

function hasOwn(value: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, key);
}

function metricsOf(raw: unknown): UsageMetrics {
  const m = record(raw);
  return {
    spend: num(m.spend),
    promptTokens: num(m.prompt_tokens),
    completionTokens: num(m.completion_tokens),
    cacheReadTokens: num(m.cache_read_input_tokens),
    cacheCreationTokens: num(m.cache_creation_input_tokens),
    // `api_requests` counts every attempt; fall back to successes when it is absent so a
    // shape change degrades to an undercount rather than to zero.
    requests: num(m.api_requests) || num(m.successful_requests),
    successfulRequests: num(m.successful_requests),
    failedRequests: num(m.failed_requests),
  };
}

function addMetrics(into: UsageMetrics, from: UsageMetrics): void {
  into.spend += from.spend;
  into.promptTokens += from.promptTokens;
  into.completionTokens += from.completionTokens;
  into.cacheReadTokens += from.cacheReadTokens;
  into.cacheCreationTokens += from.cacheCreationTokens;
  into.requests += from.requests;
  into.successfulRequests += from.successfulRequests;
  into.failedRequests += from.failedRequests;
}

export function sumMetrics(all: readonly UsageMetrics[]): UsageMetrics {
  const total = emptyMetrics();
  for (const one of all) addMetrics(total, one);
  return total;
}

/** `results[]` from either daily-activity endpoint, tolerating a bare array. */
function resultRows(payload: unknown): Record<string, unknown>[] {
  const envelope = record(payload);
  const rows = Array.isArray(envelope.results)
    ? envelope.results
    : Array.isArray(payload)
      ? payload
      : [];
  return rows.map(record);
}

/** `metadata.total_pages`, the one metadata field that is safe to trust. */
export function totalPages(payload: unknown): number {
  const pages = num(record(record(payload).metadata).total_pages);
  return pages > 0 ? pages : 1;
}

export interface ParsedTagActivity {
  rows: Map<string, TagUsageRow>;
  /** Entity keys the response carried that we did not ask for. */
  unexpectedTags: string[];
}

/**
 * Parse `/tag/daily/activity` into per-tag rows.
 *
 * `requested` is intersected against the response's entity keys. The proxy can report
 * co-tags that happen to share the queried rows, and summing those too would reintroduce
 * the double count from the other direction — so anything unasked-for is reported as
 * unexpected rather than folded into a total.
 */
export function parseTagDailyActivity(
  payload: unknown,
  requested: readonly string[],
): ParsedTagActivity {
  const wanted = new Set(requested);
  const rows = new Map<string, TagUsageRow>();
  const unexpected = new Set<string>();

  for (const row of resultRows(payload)) {
    const date = str(row.date);
    // NOTE: `row.metrics` is deliberately NOT read — see the module docstring.
    const entities = record(record(row.breakdown).entities);

    for (const [tag, rawEntity] of Object.entries(entities)) {
      if (!wanted.has(tag)) {
        unexpected.add(tag);
        continue;
      }

      const entity = record(rawEntity);
      const metrics = metricsOf(entity.metrics);
      const existing = rows.get(tag) ?? newRow(tag);

      addMetrics(existing.metrics, metrics);
      if (date) {
        existing.daily.push({
          date,
          spend: metrics.spend,
          requests: metrics.requests,
          totalTokens: metrics.promptTokens + metrics.completionTokens,
        });
      }
      accumulateOrigins(existing.origins, entity.api_key_breakdown);
      rows.set(tag, existing);
    }
  }

  for (const row of rows.values()) {
    row.daily.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
    row.origins.sort((a, b) => b.spend - a.spend);
  }

  return { rows, unexpectedTags: [...unexpected].sort() };
}

function newRow(tag: string): TagUsageRow {
  const separator = tag.indexOf(":");
  return {
    tag,
    namespace: separator > 0 ? tag.slice(0, separator) : tag,
    value: separator > 0 ? tag.slice(separator + 1) : tag,
    label: null,
    metrics: emptyMetrics(),
    daily: [],
    origins: [],
  };
}

/**
 * Fold the per-key split into `origins`.
 *
 * This is how environment and service are recovered without either being a tag: the alias
 * is minted as `<env-slug>-<backend|ai-service>`, so the key that authenticated tells us
 * both, and it cannot drift the way a self-reported tag could.
 */
function accumulateOrigins(into: KeyOrigin[], raw: unknown): void {
  for (const entry of Object.values(record(raw))) {
    const perKey = record(entry);
    const keyAlias = str(record(perKey.metadata).key_alias);
    if (!keyAlias) continue;

    const spend = num(record(perKey.metrics).spend);
    const existing = into.find((origin) => origin.keyAlias === keyAlias);
    if (existing) {
      existing.spend += spend;
      continue;
    }
    into.push({ keyAlias, ...splitKeyAlias(keyAlias), spend });
  }
}

/**
 * Parse `/team/daily/activity` into window totals — the denominator — plus a per-team
 * split for callers that group by environment (the control plane, under the master key).
 *
 * Summing `results[].metrics` **is** sound here, unlike on the tag endpoint: a request
 * belongs to exactly one team, so there is no fan-out to double-count. The same holds for
 * `breakdown.entities`, which this endpoint keys by team id.
 */
export function parseTeamDailyActivity(payload: unknown): {
  totals: UsageMetrics;
  daily: UsageDay[];
  /** Spend-descending. Empty when the payload carries no breakdown. */
  byTeam: TeamWindowUsage[];
  models: UsageDimensionRow[];
  modelGroups: UsageDimensionRow[];
  providers: UsageDimensionRow[];
  availableDimensions: AvailableUsageDimensions;
} {
  const totals = emptyMetrics();
  const daily = new Map<string, UsageDay>();
  const byTeam = new Map<string, TeamWindowUsage>();
  const models = new Map<string, UsageDimensionRow>();
  const modelGroups = new Map<string, UsageDimensionRow>();
  const providers = new Map<string, UsageDimensionRow>();
  const availableDimensions: AvailableUsageDimensions = {
    models: false,
    modelGroups: false,
    providers: false,
  };

  for (const row of resultRows(payload)) {
    const metrics = metricsOf(row.metrics);
    addMetrics(totals, metrics);

    const date = str(row.date);
    if (date) {
      const day = daily.get(date) ?? {
        date,
        spend: 0,
        requests: 0,
        totalTokens: 0,
      };
      day.spend += metrics.spend;
      day.requests += metrics.requests;
      day.totalTokens += metrics.promptTokens + metrics.completionTokens;
      daily.set(date, day);
    }

    const breakdown = record(row.breakdown);
    const entities = record(breakdown.entities);
    for (const [teamId, rawEntity] of Object.entries(entities)) {
      const entity = record(rawEntity);
      const team = byTeam.get(teamId) ?? {
        teamId,
        teamAlias: null,
        metrics: emptyMetrics(),
      };
      addMetrics(team.metrics, metricsOf(entity.metrics));
      team.teamAlias ??= str(record(entity.metadata).team_alias);
      byTeam.set(teamId, team);
    }

    availableDimensions.models ||= hasOwn(breakdown, "models");
    availableDimensions.modelGroups ||= hasOwn(breakdown, "model_groups");
    availableDimensions.providers ||= hasOwn(breakdown, "providers");
    accumulateDimension(models, breakdown.models);
    accumulateDimension(modelGroups, breakdown.model_groups);
    accumulateDimension(providers, breakdown.providers);
  }

  return {
    totals,
    daily: [...daily.values()].sort(compareDays),
    byTeam: [...byTeam.values()].sort(
      (a, b) => b.metrics.spend - a.metrics.spend,
    ),
    models: sortedDimensions(models),
    modelGroups: sortedDimensions(modelGroups),
    providers: sortedDimensions(providers),
    availableDimensions,
  };
}

function accumulateDimension(
  into: Map<string, UsageDimensionRow>,
  raw: unknown,
): void {
  for (const [name, rawEntry] of Object.entries(record(raw))) {
    const entry = record(rawEntry);
    const metrics = metricsOf(hasOwn(entry, "metrics") ? entry.metrics : entry);
    const row = into.get(name) ?? { name, metrics: emptyMetrics() };
    addMetrics(row.metrics, metrics);
    into.set(name, row);
  }
}

function sortedDimensions(
  rows: Map<string, UsageDimensionRow>,
): UsageDimensionRow[] {
  return [...rows.values()].sort((a, b) => b.metrics.spend - a.metrics.spend);
}

function compareDays(a: UsageDay, b: UsageDay): number {
  return a.date < b.date ? -1 : a.date > b.date ? 1 : 0;
}

/**
 * Tag names from `/tag/list`, tolerating a bare array or `{ tags: [...] }`.
 * `[]` is a successful empty window; `null` means the response was not a tag list.
 */
export function parseTagList(payload: unknown): string[] | null {
  const envelope = record(payload);
  const raw = Array.isArray(envelope.tags)
    ? envelope.tags
    : Array.isArray(payload)
      ? payload
      : null;
  if (raw === null) return null;
  const names = raw
    .map((entry) =>
      typeof entry === "string"
        ? str(entry)
        : str(record(entry).tag_name ?? record(entry).name),
    )
    .filter((name): name is string => name !== null);
  if (names.length !== raw.length) return null;
  return [...new Set(names)];
}

/**
 * Per-tag spend on one page of an **unfiltered** `/tag/daily/activity` response.
 *
 * This is discovery ranking, not a reported number: it decides which tags are worth a detail
 * fetch, and everything the UI renders is re-read per tag by `parseTagDailyActivity`. Reading
 * `breakdown.entities` rather than `results[].metrics` still matters for the reason in the
 * module docstring — here it is also the only thing that makes the read per-tag at all.
 *
 * One page at a time, because the endpoint pages over `LiteLLM_DailyTagSpend` rows rather
 * than over days: the same tag reappears on later pages and the caller sums across them.
 */
export function parseTagSpendByEntity(payload: unknown): Map<string, number> {
  const spendByTag = new Map<string, number>();

  for (const row of resultRows(payload)) {
    const entities = record(record(row.breakdown).entities);
    for (const [tag, rawEntity] of Object.entries(entities)) {
      const spend = num(record(record(rawEntity).metrics).spend);
      spendByTag.set(tag, (spendByTag.get(tag) ?? 0) + spend);
    }
  }

  return spendByTag;
}
